import { useCallback, useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/menu/main-menu';
import { DAY_PART_SELECTIONS } from 'state/menu/main-menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { getMinutesUntilRestaurantClose } from 'utils/restaurant';
import { routes } from 'utils/routing';

import { ISmartAlert, SmartAlert } from './types';

const MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS = 30;
const MINUTES_BEFORE_RESTAURANT_CLOSE = 30;
const MINUTES_BEFORE_PICK_UP_WINDOW_ENDS = 10;
const SMART_ALERT_REFRESH_INTERVAL = 60 * 1000;

export const useSmartAlert = (isPickupServiceMode = false): ISmartAlert => {
  const isSmartAlertsEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMART_ALERTS);
  const { formatMessage } = useIntl();
  const [tsmSmartAlert, setTsmSmartAlert] = useState<SmartAlert | null>(null);
  const [orderSmartAlert, setOrderSmartAlert] = useState<SmartAlert | null>(null);

  const { store: restaurant } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { activeDayParts } = useMainMenuContext();

  const breakfastInterval = activeDayParts.find(
    item => item?.key === DAY_PART_SELECTIONS.BREAKFAST_KEY
  );

  const generateOrderAlert = useCallback(
    (remainingMinutesToBreakfastDayPartChange: number) => {
      // Pick Up window ends
      if (
        isPickupServiceMode &&
        remainingMinutesToBreakfastDayPartChange > 0 &&
        remainingMinutesToBreakfastDayPartChange <= MINUTES_BEFORE_PICK_UP_WINDOW_ENDS
      ) {
        setOrderSmartAlert({
          message: formatMessage(
            { id: 'pickupWindow' },
            { minutes: remainingMinutesToBreakfastDayPartChange }
          ),
        });

        return;
      }

      setOrderSmartAlert(null);
    },
    [formatMessage, isPickupServiceMode]
  );

  const generateTsmAlert = useCallback(
    (remainingMinutesToBreakfastDayPartChange: number) => {
      // Breakfast DayPart ends
      if (
        remainingMinutesToBreakfastDayPartChange > 0 &&
        remainingMinutesToBreakfastDayPartChange <= MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS
      ) {
        setTsmSmartAlert({
          message: formatMessage(
            { id: 'breakfastDayPartEnds' },
            { minutes: remainingMinutesToBreakfastDayPartChange }
          ),
          linkUrl: routes.store,
        });

        return;
      }

      // Restaurant closing time
      if (serviceMode) {
        const minutesForClosing = getMinutesUntilRestaurantClose({ restaurant, serviceMode });

        if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_RESTAURANT_CLOSE) {
          setTsmSmartAlert({
            message: formatMessage({ id: 'restaurantCloses' }, { minutes: minutesForClosing }),
            linkUrl: routes.store,
          });

          return;
        }
      }

      setTsmSmartAlert(null);
    },
    [formatMessage, restaurant, serviceMode]
  );

  const generateAlerts = useCallback(() => {
    // TODO: Replace `new Date()` with the store's current date based on its timezone,
    // once `activeDayParts` are also calculated using that same timezone.
    const remainingMinutesToBreakfastDayPartChange = breakfastInterval?.endTime
      ? differenceInMinutes(breakfastInterval?.endTime, new Date())
      : 0;

    generateOrderAlert(remainingMinutesToBreakfastDayPartChange);
    generateTsmAlert(remainingMinutesToBreakfastDayPartChange);
  }, [breakfastInterval?.endTime, generateOrderAlert, generateTsmAlert]);

  useEffect(() => {
    if (!isSmartAlertsEnabled) {
      return;
    }

    const interval = setInterval(() => {
      generateAlerts();
    }, SMART_ALERT_REFRESH_INTERVAL);

    generateAlerts();

    return () => {
      clearInterval(interval);
    };
  }, [generateAlerts, isSmartAlertsEnabled]);

  return {
    tsmSmartAlert,
    orderSmartAlert,
  };
};
